import { Component, signal, effect, HostListener, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MagicSearchService, SearchPointer } from '../../service/magic-search/magic-search.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';

@Component({
  selector: 'magic-searchbar-v2',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './magic-searchbar-v2.component.html',
  styleUrl: './magic-searchbar-v2.component.css'
})
export class MagicSearchbarV2Component {
  @ViewChild('triggerInput', { static: false }) triggerInput?: ElementRef<HTMLInputElement>;

  public magicSearchService = inject(MagicSearchService);
  private currentUserService = inject(CurrentUserService);

  isOpen = signal<boolean>(false);
  showAIBanner = signal<boolean>(true);
  recentSearches = signal<string[]>([
    'in:devsquad disposable email',
    'What did I accomplish this week?',
    'christmas dates',
    'in:@Alex Gray-Johnson bolt',
    'in:@Alex Gray-Johnson batch'
  ]);
  searchScope = signal<string>('devsquad');

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Open search with Cmd/Ctrl + K
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.openSearch();
    }
    // Close search with Escape
    if (event.key === 'Escape' && this.isOpen()) {
      this.closeSearch();
    }
  }

  openSearch() {
    this.isOpen.set(true);
    // Focus the search input after opening
    setTimeout(() => {
      const input = document.getElementById('magic-search-input') as HTMLInputElement;
      if (input) {
        input.focus();
        // Move cursor to end of input if there's existing text
        if (this.magicSearchService.searchQuery()) {
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }
    }, 100);
  }

  closeSearch() {
    this.isOpen.set(false);
    // Don't clear the search query, keep it in the trigger input
  }

  dismissAIBanner() {
    this.showAIBanner.set(false);
  }

  selectRecentSearch(search: string) {
    this.magicSearchService.searchQuery.set(search);
    // Focus back on input
    const input = document.getElementById('magic-search-input') as HTMLInputElement;
    if (input) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }

  onBackspace(event: any) {
    console.log('Backspace ---->');
    console.log('Search Query:', this.magicSearchService.searchQuery());
    if (this.magicSearchService.searchQuery() === '') {
      this.magicSearchService.selectedPointer.set(this.magicSearchService.defaultPointer);
      this.magicSearchService.searchQuery.set('');
      this.magicSearchService.searchColour.set('pink');
    }
  }

  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (value === '' || value === null || value === undefined) {
      this.magicSearchService.selectedPointer.set(this.magicSearchService.defaultPointer);
      this.magicSearchService.searchQuery.set('');
      this.magicSearchService.searchColour.set('pink');
      // this.runDefaultSearch();
      return;
    }

    if (value === '@') {
      this.magicSearchService.selectedPointer.set(this.magicSearchService.blue_pointers.find(pointer => pointer.label.toLowerCase() === 'handle:') || this.magicSearchService.defaultPointer);
      this.magicSearchService.searchQuery.set('');
      this.magicSearchService.searchColour.set('blue');
      // Clear the input field
      target.value = '';
      return;
    }

    // Check if the value exactly matches a pointer (case-insensitive)
    const matchedPinkPointer = this.magicSearchService.pink_pointers.find(pointer => pointer.label.toLowerCase() === value.toLowerCase());
    const matchedBluePointer = this.magicSearchService.blue_pointers.find(pointer => pointer.label.toLowerCase() === value.toLowerCase());
    const matchedPurplePointer = this.magicSearchService.purple_pointers.find(pointer => pointer.label.toLowerCase() === value.toLowerCase());

    if (matchedPinkPointer) {
      this.magicSearchService.selectedPointer.set(matchedPinkPointer);
      this.magicSearchService.searchQuery.set('');
      this.magicSearchService.searchColour.set('pink');
      // Clear the input field
      target.value = '';
      return;
    }

    if (matchedBluePointer) {
      this.magicSearchService.selectedPointer.set(matchedBluePointer);
      this.magicSearchService.searchQuery.set('');
      this.magicSearchService.searchColour.set('blue');
      // Clear the input field
      target.value = '';
      return;
    }

    if (matchedPurplePointer) {
      this.magicSearchService.selectedPointer.set(matchedPurplePointer);
      this.magicSearchService.searchQuery.set('');
      this.magicSearchService.searchColour.set('purple');
      // Clear the input field
      target.value = '';
      return;
    }

    // No pointer matched, continue with normal search logic
    if (this.magicSearchService.selectedPointer() || value === '') {
      switch (this.magicSearchService.searchColour()) {
        case 'pink':
          // Don't do anything -> Wait for enter.
          break;
        case 'blue':
          console.log('KICK OFF => Blue Search...');
          // this.magicSearchService.kickOffBlueSearch();§
          break;
        case 'purple':
          // Don't do anything -> Wait for enter.
          break;
        default:
          // Pink By Default...
          break;
      }
    }

    // Update searchQuery with the current input value
    this.magicSearchService.searchQuery.set(target.value);
  }

  performSearch() {
    if (this.magicSearchService.searchQuery()?.trim()) {
      // Add to recent searches if not already there
      const currentQuery = this.magicSearchService.searchQuery()?.trim();
      const recent = this.recentSearches();
      if (currentQuery && !recent.includes(currentQuery)) {
        this.recentSearches.set([currentQuery, ...recent.slice(0, 4)]);
      }
      // Perform search logic here
      console.log('Searching for:', currentQuery);
      console.log('Search Colour:', this.magicSearchService.searchColour());
      this.isOpen.set(false);

      switch (this.magicSearchService.searchColour()) {
        case 'pink':
          this.magicSearchService.kickOffPinkSearch();
          break;
        case 'blue':
          this.magicSearchService.kickOffBlueSearch();
          break;
        case 'purple':
          this.magicSearchService.kickOffPurpleSearch();
          break;
        default:
          // this.magicSearchService.kickOffPurpleSearch();
          break;
      }
    }
  }

  isAdminOnlyBluePointer(pointer: SearchPointer): boolean {
    if (pointer.label === 'email:' || pointer.label === 'user id:') {
      return !this.currentUserService.hasAnyRole([6]);
    }
    return false;
  }

  selectPointer(pointer: SearchPointer) {
    this.magicSearchService.selectedPointer.set(pointer);
    this.magicSearchService.searchColour.set(pointer.class.split('-')[1] as 'pink' | 'blue' | 'purple');
  }

  setLuckyPointer() {
    const luckyPointer = this.magicSearchService.pink_pointers.find(p => p.label === 'lucky:');
    if (luckyPointer) {
      this.magicSearchService.selectedPointer.set(luckyPointer);
      this.magicSearchService.searchColour.set('pink');
    }
  }
}
